/**
 * 7. Inbox / Approvals — task-centric inbox of approvals, assignments,
 * SLA escalations; grouped by Action Required / Waiting on Others /
 * Updates; filters, batch approve, open record.
 *
 * Arabic and English (C7): the tabs are chosen by id, not by their English
 * label, and a risk or a record type is shown as a word, never the stored
 * value.
 *
 * The list is GET /v1/approval-requests?assignee=me (CTL-0, D208): the
 * requests with a step waiting on the signed-in person, each row carrying
 * that step's `assignment_id`, which Approve and Reject decide through. It
 * used to read GET /v1/approvals, whose rows carry no assignment id, so both
 * buttons posted to /approval-assignments/undefined/decide.
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  Badge,
  Button,
  DataTable,
  Skeleton,
  Tabs,
} from "@/components/ui";
import { useApiList, useApiMutation, post } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { recordTypeLabel } from "@/lib/i18n/records";
import { formatDate } from "@/lib/money";

interface ApprovalTask {
  id: string;
  entityType: string;
  entityId: string;
  /** The request's reason, e.g. "Purchase order PO-0007". */
  reason?: string | null;
  title?: string;
  submittedBy: string;
  /** The submitter's display name (the API's submitted_by_name). */
  submittedByName?: string | null;
  submittedAt: string;
  slaDueAt: string | null;
  risk: "Low" | "Medium" | "High";
  state: "Pending" | "Approved" | "Rejected" | "Escalated";
  assignmentId: string;
}

type InboxGroup = "actionRequired" | "waitingOnOthers" | "updates";
const GROUPS: InboxGroup[] = ["actionRequired", "waitingOnOthers", "updates"];

export function InboxPage() {
  const { t } = useTranslation();
  const [group, setGroup] = useState<string>("actionRequired");
  const { data, isLoading } = useApiList<ApprovalTask>(
    ["approvals", "pending"],
    "/approval-requests?state=Pending&assignee=me&limit=50",
  );
  const decide = useApiMutation(
    ["approvals"],
    (input: {
      assignmentId: string;
      decision: "approve" | "reject";
      comment?: string;
    }) =>
      post(`/approval-assignments/${input.assignmentId}/decide`, {
        decision: input.decision,
        comment: input.comment,
      }),
  );

  if (isLoading) return <Skeleton rows={8} />;

  const tasks = data?.items ?? [];
  const grouped =
    group === "actionRequired"
      ? tasks.filter((task) => task.state === "Pending")
      : tasks;

  return (
    <div>
      <PageHeader title={t("inbox.title")} subtitle={t("inbox.subtitle")} />
      <Tabs
        tabs={GROUPS.map((id) => ({ id, label: t(`inbox.tabs.${id}`) }))}
        active={group}
        onChange={setGroup}
      />
      <Card title={t("inbox.pendingTasks", { count: grouped.length })}>
        <DataTable
          rows={grouped}
          keyField="id"
          columns={[
            {
              key: "title",
              label: t("inbox.columns.task"),
              render: (r) => r.title ?? r.reason ?? "—",
            },
            {
              key: "entityType",
              label: t("inbox.columns.type"),
              render: (r) =>
                r.entityType ? recordTypeLabel(t, r.entityType) : "—",
            },
            {
              key: "submittedBy",
              label: t("inbox.columns.requestedBy"),
              render: (r) => r.submittedByName ?? r.submittedBy,
            },
            {
              key: "submittedAt",
              label: t("inbox.columns.received"),
              render: (r) => formatDate(r.submittedAt),
            },
            {
              key: "slaDueAt",
              label: t("inbox.columns.slaDue"),
              render: (r) =>
                r.slaDueAt ? (
                  <Badge
                    tone={
                      r.slaDueAt < new Date().toISOString()
                        ? "danger"
                        : "warning"
                    }
                  >
                    {formatDate(r.slaDueAt)}
                  </Badge>
                ) : (
                  "—"
                ),
            },
            {
              key: "risk",
              label: t("inbox.columns.risk"),
              render: (r) => (
                <Badge
                  tone={
                    r.risk === "High"
                      ? "danger"
                      : r.risk === "Medium"
                        ? "warning"
                        : "default"
                  }
                >
                  {r.risk
                    ? t(`inbox.risk.${r.risk}`, { defaultValue: r.risk })
                    : "—"}
                </Badge>
              ),
            },
            {
              key: "actions",
              label: t("inbox.columns.actions"),
              render: (r) => (
                <div style={{ display: "flex", gap: 6 }}>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() =>
                      void decide.mutateAsync({
                        assignmentId: r.assignmentId,
                        decision: "approve",
                      })
                    }
                  >
                    {t("inbox.approve")}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() =>
                      void decide.mutateAsync({
                        assignmentId: r.assignmentId,
                        decision: "reject",
                        comment: "Rejected from inbox",
                      })
                    }
                  >
                    {t("inbox.reject")}
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
